import { createRouter, createWebHistory } from 'vue-router'
import Swal from 'sweetalert2'
import store from '@/store'
import { validateSessionAgainstRTDB } from '@/utils/sessionGuard'
import { COUPON_IMPORT_ROLES } from '@/utils/couponAccess'
import { CART_INVENTORY_SYNC_ROLES } from '@/utils/cartInventorySyncAccess'
import BodyView from '@/layout/BodyView.vue'
import indexDashbord from '@/pages/indexDashbord.vue'
import loginValidation from '@/pages/authentication/loginValidation.vue'
import consultaUsuarios from '@/pages/users/consulta.vue'

const LIMITED_ROLES = ['Servicio a Domicilio', 'Transportista']
const PUBLIC_AUTH_PATHS = ['/login', '/authentication/forgetpassword']

const routes = [
  {
    path: '/login',
    name: 'login',
    component: loginValidation,
    meta: {
      title: 'Login - Super Kompras Backoffice',
      requiresAuth: false,
    },
  },
  {
    path: '/authentication/forgetpassword',
    name: 'resetPassword',
    component: () => import('@/pages/authentication/resetPassword.vue'),
    meta: {
      title: 'Recuperar Contraseña - Super Kompras Backoffice',
      requiresAuth: false,
    },
  },
  {
    path: '/',
    component: BodyView,
    meta: {
      requiresAuth: true,
    },
    children: [
      {
        path: '/',
        redirect: to => {
          // Redirigir según el rol del usuario
          const authUser = localStorage.getItem('auth_user')
          if (authUser) {
            try {
              const user = JSON.parse(authUser)
              const userRole = user.rol

              // Roles con acceso limitado se redirigen a pedidos
              if (LIMITED_ROLES.includes(userRole)) {
                return '/pedidos'
              }
            } catch (error) {
              console.error('Error parsing user data:', error)
            }
          }
          // Por defecto, redirigir al dashboard
          return '/dashboard'
        },
      },
      {
        path: '/dashboard',
        name: 'dashboard',
        component: indexDashbord,
        meta: {
          title: 'Dashboard - Super Kompras Backoffice',
          requiresAuth: true,
          restrictedRoles: [...LIMITED_ROLES],
        },
      },
      {
        path: '/users/consulta',
        name: 'consultaUsuarios',
        component: consultaUsuarios,
        meta: {
          title: 'Consulta de Usuarios - Super Kompras Backoffice',
          requiresAuth: true,
        },
      },
      {
        path: '/users/alta',
        name: 'altaUsuarios',
        component: () => import('@/pages/users/alta.vue'),
        meta: {
          title: 'Alta de Usuarios - Super Kompras Backoffice',
          requiresAuth: true,
        },
      },
      {
        path: '/users/configuracion',
        name: 'configuracionUsuario',
        component: () => import('@/pages/users/configuracion.vue'),
        meta: {
          title: 'Configuración - Super Kompras Backoffice',
          requiresAuth: true,
        },
      },
      {
        path: '/users/profile',
        name: 'userProfile',
        component: () => import('@/pages/users/profile.vue'),
        meta: {
          title: 'Mi Perfil - Super Kompras Backoffice',
          requiresAuth: true,
        },
      },
      {
        path: '/users/edit',
        name: 'editUsuario',
        redirect: '/users/configuracion',
      },
      {
        path: '/coupons/consulta',
        name: 'consultaCupones',
        component: () => import('@/pages/coupons/consulta.vue'),
        meta: {
          title: 'Consulta de Cupones - Super Kompras Backoffice',
          requiresAuth: true,
          restrictedRoles: [...LIMITED_ROLES],
        },
      },
      {
        path: '/coupons/alta',
        name: 'altaCupones',
        component: () => import('@/pages/coupons/alta.vue'),
        meta: {
          title: 'Alta de Cupones - Super Kompras Backoffice',
          requiresAuth: true,
          restrictedRoles: [...LIMITED_ROLES],
        },
      },
      {
        path: '/coupons/importar',
        name: 'importarCupones',
        component: () => import('@/pages/coupons/importar.vue'),
        meta: {
          title: 'Importar cupones - Super Kompras Backoffice',
          requiresAuth: true,
          requiredRoles: [...COUPON_IMPORT_ROLES],
        },
      },
      {
        path: '/coupons/actualizar-vigencias',
        name: 'actualizarVigencias',
        component: () => import('@/pages/coupons/actualizarVigencias.vue'),
        meta: {
          title: 'Actualizar vigencias - Super Kompras Backoffice',
          requiresAuth: true,
          requiredRoles: [...COUPON_IMPORT_ROLES],
        },
      },
      {
        path: '/coupons/canjes',
        name: 'consultaCanjes',
        component: () => import('@/pages/coupons/canjes.vue'),
        meta: {
          title: 'Consulta De Canjes - Super Kompras Backoffice',
          requiresAuth: true,
          restrictedRoles: [...LIMITED_ROLES],
        },
      },
      {
        path: '/coupons/products',
        name: 'couponProducts',
        component: () => import('@/pages/coupons/CouponProducts.vue'),
        meta: {
          title: 'Productos de Cupón - Super Kompras Backoffice',
          requiresAuth: true,
          restrictedRoles: [...LIMITED_ROLES],
        },
      },
      {
        path: '/admin/banners',
        name: 'bannersAdmin',
        component: () => import('@/pages/admin/banners.vue'),
        meta: {
          title: 'Administración de Banners - Super Kompras Backoffice',
          requiresAuth: true,
          restrictedRoles: [...LIMITED_ROLES],
        },
      },
      {
        path: '/admin/banners/products',
        name: 'bannerProducts',
        component: () => import('@/pages/admin/BannerProducts.vue'),
        meta: {
          title: 'Productos de Banner - Super Kompras Backoffice',
          requiresAuth: true,
          restrictedRoles: [...LIMITED_ROLES],
        },
      },
      {
        path: '/admin/pdf-offers',
        name: 'pdfOffersAdmin',
        component: () => import('@/pages/admin/pdfOffers.vue'),
        meta: {
          title: 'PDF Oferta Quincenal - Super Kompras Backoffice',
          requiresAuth: true,
          restrictedRoles: [...LIMITED_ROLES],
        },
      },
      {
        path: '/admin/departments',
        name: 'departmentsAdmin',
        component: () => import('@/pages/admin/departments.vue'),
        meta: {
          title: 'Departamentos - Super Kompras Backoffice',
          requiresAuth: true,
          restrictedRoles: [...LIMITED_ROLES],
        },
      },
      {
        path: '/admin/app-version-ios',
        name: 'appVersionIos',
        component: () => import('@/pages/admin/appVersionIos.vue'),
        meta: {
          title: 'Versión mínima iOS - Super Kompras Backoffice',
          requiresAuth: true,
          requiredRoles: ['Administrador'],
        },
      },
      {
        path: '/admin/announcement',
        name: 'announcementAdmin',
        component: () => import('@/pages/admin/announcement.vue'),
        meta: {
          title: 'Comunicado informativo - Super Kompras Backoffice',
          requiresAuth: true,
          requiredRoles: ['Administrador', 'Soporte', 'E-commerce'],
        },
      },
      {
        path: '/home-sections',
        name: 'homeSectionsList',
        component: () => import('@/pages/home-sections/HomeSectionsList.vue'),
        meta: {
          title: 'Secciones Destacadas - Super Kompras Backoffice',
          requiresAuth: true,
          restrictedRoles: [...LIMITED_ROLES],
        },
      },
      {
        path: '/home-sections/products',
        name: 'sectionProducts',
        component: () => import('@/pages/home-sections/SectionProducts.vue'),
        meta: {
          title: 'Gestión de Productos - Super Kompras Backoffice',
          requiresAuth: true,
          restrictedRoles: [...LIMITED_ROLES],
        },
      },
      {
        path: '/inventory',
        name: 'inventory',
        component: () => import('@/pages/inventory/index.vue'),
        meta: {
          title: 'Inventario - Super Kompras Backoffice',
          requiresAuth: true,
          restrictedRoles: ['Transportista'],
        },
      },
      {
        path: '/synonyms',
        name: 'synonyms',
        component: () => import('@/pages/synonyms/index.vue'),
        meta: {
          title: 'Sinónimos - Super Kompras Backoffice',
          requiresAuth: true,
          restrictedRoles: ['Transportista'],
        },
      },
      {
        path: '/inventory/stock',
        name: 'productStock',
        component: () => import('@/pages/inventory/ProductStock.vue'),
        meta: {
          title: 'Stock e Inventario - Super Kompras Backoffice',
          requiresAuth: true,
          restrictedRoles: ['Transportista'],
        },
      },
      {
        path: '/operations/inventory-sync',
        name: 'inventorySync',
        component: () => import('@/pages/operations/inventorySync.vue'),
        meta: {
          title: 'Sincronización de Inventario - Super Kompras Backoffice',
          requiresAuth: true,
          requiredRoles: [...CART_INVENTORY_SYNC_ROLES],
        },
      },
      {
        path: '/stores',
        name: 'stores',
        component: () => import('@/pages/stores/list.vue'),
        meta: {
          title: 'Tiendas - Super Kompras Backoffice',
          requiresAuth: true,
          requiredRoles: ['Administrador', 'Soporte'],
        },
      },
      {
        path: '/stores/:storeId/scheduling',
        name: 'storeScheduling',
        component: () => import('@/pages/stores/scheduling.vue'),
        meta: {
          title: 'Programación de tienda - Super Kompras Backoffice',
          requiresAuth: true,
          requiredRoles: ['Administrador', 'Soporte'],
        },
      },
      {
        path: '/pedidos',
        name: 'pedidos',
        component: () => import('@/pages/pedidos/index.vue'),
        meta: {
          title: 'Pedidos - Super Kompras Backoffice',
          requiresAuth: true,
        },
      },
      {
        path: '/pedidos/:key',
        name: 'pedidoDetail',
        component: () => import('@/pages/pedidos/detail.vue'),
        meta: {
          title: 'Detalle de Pedido - Super Kompras Backoffice',
          requiresAuth: true,
          fromPedidos: true, // Flag to identify this route comes from pedidos
        },
      },
      {
        path: '/pedidos/:key/product-search',
        name: 'pedidoProductSearch',
        component: () => import('@/pages/pedidos/product-search.vue'),
        meta: {
          title: 'Buscar Producto - Super Kompras Backoffice',
          requiresAuth: true,
        },
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: to => {
      // Redirigir según el rol del usuario para rutas no encontradas
      const authUser = localStorage.getItem('auth_user')
      if (authUser) {
        try {
          const user = JSON.parse(authUser)
          const userRole = user.rol

          // Roles con acceso limitado se redirigen a pedidos
          if (LIMITED_ROLES.includes(userRole)) {
            return '/pedidos'
          }
        } catch (error) {
          console.error('Error parsing user data:', error)
        }
      }
      // Por defecto, redirigir al dashboard
      return '/dashboard'
    },
  },
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  linkExactActiveClass: 'active',
  scrollBehavior(to, from, savedPosition) {
    // Si hay una posición guardada (navegación con botón atrás/adelante), restaurarla
    if (savedPosition) {
      return savedPosition
    }
    // Si viene del detalle de pedido, no hacer scroll automático
    // (el componente manejará la restauración del scroll)
    if (from.name === 'pedidoDetail' && to.name === 'pedidos') {
      return false // No hacer scroll automático
    }
    // Si hay un hash en la ruta, hacer scroll al elemento
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    }
    // Por defecto, hacer scroll al inicio de la página
    return { top: 0, behavior: 'smooth' }
  },
})

// Route guard global para autenticación
router.beforeEach(async (to, from, next) => {
  // Si viene del detalle de pedido, agregar flag en query para restaurar estado
  if (from.name === 'pedidoDetail' && to.name === 'pedidos') {
    if (!to.query) {
      to.query = {}
    }
    to.query.from = 'detail'
  }

  // Actualizar título de la página
  if (typeof to.meta.title === 'string') {
    document.title = to.meta.title
  }

  const isPublicRoute = PUBLIC_AUTH_PATHS.includes(to.path)
  const isAuthenticated = localStorage.getItem('auth_user') !== null

  // Revalidar sesión contra RTDB cuando hay credenciales locales (Fase 3)
  if (isAuthenticated) {
    try {
      const storedUser = JSON.parse(localStorage.getItem('auth_user'))
      const sessionCheck = await validateSessionAgainstRTDB(storedUser?.uid)

      if (!sessionCheck.valid) {
        await store.dispatch('auth/forceLogout', {
          reason: sessionCheck.reason,
          internalDetail: sessionCheck.internalDetail,
          redirect: false,
        })

        if (isPublicRoute) {
          next()
        } else {
          next({
            path: '/login',
            query: sessionCheck.reason ? { reason: sessionCheck.reason } : undefined,
          })
        }
        return
      }

      if (sessionCheck.userData) {
        store.commit('auth/syncUserFromRTDB', sessionCheck.userData)
      }
    } catch (error) {
      console.error('Error validating session against RTDB:', error)
      await store.dispatch('auth/forceLogout', { redirect: false })
      if (isPublicRoute) {
        next()
      } else {
        next('/login')
      }
      return
    }
  }

  // Verificar si la ruta requiere autenticación
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)

  // Si la ruta requiere autenticación y el usuario no está autenticado
  if (requiresAuth && !isAuthenticated) {
    next('/login')
    return
  }

  // Verificar si la ruta requiere roles específicos
  const requiredRoles = to.matched.find(record => record.meta.requiredRoles)
  if (requiredRoles && requiredRoles.meta.requiredRoles) {
    const authUser = localStorage.getItem('auth_user')
    if (authUser) {
      try {
        const user = JSON.parse(authUser)
        const userRole = user.rol
        const allowedRoles = requiredRoles.meta.requiredRoles

        if (!allowedRoles.includes(userRole)) {
          // Usuario no tiene el rol requerido, mostrar toast y redirigir según el rol
          if (LIMITED_ROLES.includes(userRole)) {
            Swal.fire({
              title: 'Acceso Restringido',
              text: 'No tienes permisos para acceder a esta sección.',
              icon: 'warning',
              timer: 3000,
              showConfirmButton: false,
              toast: true,
              position: 'top-end',
            })
            next('/pedidos')
          } else {
            next('/dashboard')
          }
          return
        }
      } catch (error) {
        console.error('Error parsing user data:', error)
        next('/login')
        return
      }
    } else {
      next('/login')
      return
    }
  }

  // Verificar si la ruta restringe roles específicos
  const restrictedRoles = to.matched.find(record => record.meta.restrictedRoles)
  if (restrictedRoles && restrictedRoles.meta.restrictedRoles) {
    const authUser = localStorage.getItem('auth_user')
    if (authUser) {
      try {
        const user = JSON.parse(authUser)
        const userRole = user.rol
        const restricted = restrictedRoles.meta.restrictedRoles

        if (restricted.includes(userRole)) {
          // Usuario tiene un rol restringido, mostrar toast y redirigir según el rol
          if (LIMITED_ROLES.includes(userRole)) {
            Swal.fire({
              title: 'Acceso Restringido',
              text: 'No tienes permisos para acceder a esta sección.',
              icon: 'warning',
              timer: 3000,
              showConfirmButton: false,
              toast: true,
              position: 'top-end',
            })
            next('/pedidos')
          } else {
            next('/dashboard')
          }
          return
        }
      } catch (error) {
        console.error('Error parsing user data:', error)
        next('/login')
        return
      }
    } else {
      next('/login')
      return
    }
  }

  // Si el usuario está autenticado y trata de ir a login o reset password, redirigir según el rol
  if ((to.path === '/login' || to.path === '/authentication/forgetpassword') && isAuthenticated) {
    const authUser = localStorage.getItem('auth_user')
    if (authUser) {
      try {
        const user = JSON.parse(authUser)
        const userRole = user.rol

        // Roles con acceso limitado se redirigen a pedidos
        if (LIMITED_ROLES.includes(userRole)) {
          next('/pedidos')
        } else {
          next('/dashboard')
        }
      } catch (error) {
        console.error('Error parsing user data:', error)
        next('/dashboard')
      }
    } else {
      next('/dashboard')
    }
    return
  }

  next()
})

export default router
